import React, { useState, useEffect } from 'react';
import {
  CreditCard,
  Key,
  Settings,
  Save,
  Eye,
  EyeOff,
  AlertCircle,
  CheckCircle,
  TestTube,
  Globe
} from 'lucide-react';

export default function MpesaCredentialManagement() {
  const [credentials, setCredentials] = useState({
    sandbox: null,
    live: null
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);
  const [showPasswords, setShowPasswords] = useState({
    sandbox: false,
    live: false
  });

  // Form states
  const [sandboxForm, setSandboxForm] = useState({
    consumerKey: '',
    consumerSecret: '',
    shortcode: '',
    passkey: '',
    transactionType: 'CustomerPayBillOnline',
    storeNumber: ''
  });

  const [liveForm, setLiveForm] = useState({
    consumerKey: '',
    consumerSecret: '',
    shortcode: '',
    passkey: '',
    transactionType: 'CustomerPayBillOnline',
    storeNumber: ''
  });

  const fetchCredentials = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');

      const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'https://api.intuitlms.online/api';
      const response = await fetch(`${API_BASE_URL}/mpesa-credentials`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        throw new Error('Failed to fetch M-Pesa credentials');
      }

      const creds = await response.json();

      // Organize credentials by environment
      const organizedCreds = {
        sandbox: creds.find(c => c.environment === 'sandbox') || null,
        live: creds.find(c => c.environment === 'live') || null
      };

      setCredentials(organizedCreds);

      // Populate forms
      if (organizedCreds.sandbox) {
        setSandboxForm({
          consumerKey: organizedCreds.sandbox.consumerKey,
          consumerSecret: organizedCreds.sandbox.consumerSecret,
          shortcode: organizedCreds.sandbox.shortcode,
          passkey: organizedCreds.sandbox.passkey,
          transactionType: organizedCreds.sandbox.transactionType || 'CustomerPayBillOnline',
          storeNumber: organizedCreds.sandbox.storeNumber || ''
        });
      }

      if (organizedCreds.live) {
        setLiveForm({
          consumerKey: organizedCreds.live.consumerKey,
          consumerSecret: organizedCreds.live.consumerSecret,
          shortcode: organizedCreds.live.shortcode,
          passkey: organizedCreds.live.passkey,
          transactionType: organizedCreds.live.transactionType || 'CustomerPayBillOnline',
          storeNumber: organizedCreds.live.storeNumber || ''
        });
      }

    } catch (error) {
      setError(error.message);
      console.error('Error fetching credentials:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCredentials();
  }, []);

  const handleSaveCredentials = async (environment, formData) => {
    try {
      setSaving(true);
      setError(null);

      const token = localStorage.getItem('token');
      const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'https://api.intuitlms.online/api';
      const response = await fetch(`${API_BASE_URL}/mpesa-credentials`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({
          environment,
          ...formData
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to save credentials');
      }

      const savedCredential = await response.json();

      // Update local state
      setCredentials(prev => ({
        ...prev,
        [environment]: savedCredential
      }));

      // Show success message
      alert(`${environment.charAt(0).toUpperCase() + environment.slice(1)} credentials saved successfully!`);

    } catch (error) {
      setError('Failed to save credentials: ' + error.message);
      console.error('Error saving credentials:', error);
    } finally {
      setSaving(false);
    }
  };

  const togglePasswordVisibility = (environment) => {
    setShowPasswords(prev => ({
      ...prev,
      [environment]: !prev[environment]
    }));
  };

  const handleActivateCredentials = async (environment) => {
    try {
      setSaving(true);
      setError(null);

      const token = localStorage.getItem('token');
      const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'https://api.intuitlms.online/api';
      const response = await fetch(`${API_BASE_URL}/mpesa-credentials/${environment}/activate`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to activate environment');
      }

      const result = await response.json();

      // Refresh credentials list to show updated active status
      fetchCredentials();

      alert(result.message);

    } catch (error) {
      setError('Failed to activate environment: ' + error.message);
      console.error('Error activating environment:', error);
    } finally {
      setSaving(false);
    }
  };

  const CredentialCard = ({ environment, title, icon: Icon, formData, setFormData, credential }) => {
    const isConfigured = credential !== null;

    return (
      <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-200">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center space-x-3">
            <div className={`w-12 h-12 rounded-lg flex items-center justify-center ${environment === 'sandbox'
              ? 'bg-orange-100 text-orange-600'
              : 'bg-green-100 text-green-600'
              }`}>
              <Icon className="h-6 w-6" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-gray-900">{title}</h3>
              <p className="text-sm text-gray-500">
                {environment === 'sandbox' ? 'Testing environment' : 'Production environment'}
              </p>
            </div>
          </div>

          <div className="flex items-center space-x-2">
            {isConfigured ? (
              <div className="flex flex-col items-end">
                <div className="flex items-center text-green-600">
                  <CheckCircle className="h-4 w-4 mr-1" />
                  <span className="text-sm font-medium">Configured</span>
                </div>
                {credential.isActive && (
                  <div className="mt-1 px-2 py-0.5 bg-blue-100 text-blue-700 text-[10px] font-bold rounded-full uppercase tracking-wider">
                    Currently Active
                  </div>
                )}
              </div>
            ) : (
              <div className="flex items-center text-orange-600">
                <AlertCircle className="h-4 w-4 mr-1" />
                <span className="text-sm font-medium">Not Configured</span>
              </div>
            )}
          </div>
        </div>

        <form onSubmit={(e) => {
          e.preventDefault();
          handleSaveCredentials(environment, formData);
        }} className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Consumer Key
              </label>
              <input
                type="text"
                value={formData.consumerKey}
                onChange={(e) => setFormData(prev => ({ ...prev, consumerKey: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="Enter consumer key"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Consumer Secret
              </label>
              <div className="relative">
                <input
                  type={showPasswords[environment] ? "text" : "password"}
                  value={formData.consumerSecret}
                  onChange={(e) => setFormData(prev => ({ ...prev, consumerSecret: e.target.value }))}
                  className="w-full px-3 py-2 pr-10 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Enter consumer secret"
                  required
                />
                <button
                  type="button"
                  onClick={() => togglePasswordVisibility(environment)}
                  className="absolute inset-y-0 right-0 pr-3 flex items-center"
                >
                  {showPasswords[environment] ? (
                    <EyeOff className="h-4 w-4 text-gray-400" />
                  ) : (
                    <Eye className="h-4 w-4 text-gray-400" />
                  )}
                </button>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Shortcode
              </label>
              <input
                type="text"
                value={formData.shortcode}
                onChange={(e) => setFormData(prev => ({ ...prev, shortcode: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="Enter paybill/till number"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Passkey
              </label>
              <div className="relative">
                <input
                  type={showPasswords[environment] ? "text" : "password"}
                  value={formData.passkey}
                  onChange={(e) => setFormData(prev => ({ ...prev, passkey: e.target.value }))}
                  className="w-full px-3 py-2 pr-10 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Enter passkey"
                  required
                />
                <button
                  type="button"
                  onClick={() => togglePasswordVisibility(environment)}
                  className="absolute inset-y-0 right-0 pr-3 flex items-center"
                >
                  {showPasswords[environment] ? (
                    <EyeOff className="h-4 w-4 text-gray-400" />
                  ) : (
                    <Eye className="h-4 w-4 text-gray-400" />
                  )}
                </button>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Transaction Type
              </label>
              <select
                value={formData.transactionType}
                onChange={(e) => setFormData(prev => ({ ...prev, transactionType: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                required
              >
                <option value="CustomerPayBillOnline">Paybill (CustomerPayBillOnline)</option>
                <option value="CustomerBuyGoodsOnline">Buy Goods (CustomerBuyGoodsOnline)</option>
              </select>
              <p className="mt-1 text-[10px] text-gray-500">
                Choose Buy Goods if using a Till number
              </p>
            </div>

            {formData.transactionType === 'CustomerBuyGoodsOnline' && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Store Number
                </label>
                <input
                  type="text"
                  value={formData.storeNumber}
                  onChange={(e) => setFormData(prev => ({ ...prev, storeNumber: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Enter store number"
                  required={formData.transactionType === 'CustomerBuyGoodsOnline'}
                />
                <p className="mt-1 text-[10px] text-gray-500">
                  Required for Buy Goods. Often different from the Till Number.
                </p>
              </div>
            )}
          </div>

          <div className="flex justify-end space-x-3 pt-4">
            {isConfigured && !credential.isActive && (
              <button
                type="button"
                onClick={() => {
                  if (environment === 'live' && !window.confirm('Switching to LIVE environment will process REAL transactions. Proceed?')) {
                    return;
                  }
                  handleActivateCredentials(environment);
                }}
                disabled={saving}
                className="flex items-center px-4 py-2 border border-blue-600 text-blue-600 rounded-lg hover:bg-blue-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <Globe className="h-4 w-4 mr-2" />
                Activate {environment.charAt(0).toUpperCase() + environment.slice(1)}
              </button>
            )}
            <button
              type="submit"
              disabled={saving}
              className="flex items-center px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <Save className="h-4 w-4 mr-2" />
              {saving ? 'Saving...' : 'Save Credentials'}
            </button>
          </div>
        </form>

        {credential && (
          <div className="mt-4 pt-4 border-t border-gray-100">
            <div className="text-xs text-gray-500">
              Last updated: {new Date(credential.updatedAt).toLocaleString()}
            </div>
          </div>
        )}
      </div>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header Section */}
      <div className="bg-gradient-to-r from-blue-600 to-purple-700 rounded-xl p-6 text-white">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold flex items-center">
              <CreditCard className="mr-3 h-8 w-8" />
              M-Pesa Credentials
            </h1>
            <p className="mt-2 text-blue-100">
              Configure M-Pesa API credentials for sandbox testing and live production
            </p>
          </div>
          <div className="text-right">
            <div className="text-2xl font-bold">
              {Object.values(credentials).filter(Boolean).length}/2
            </div>
            <div className="text-blue-100">Environments Configured</div>
          </div>
        </div>
      </div>

      {/* Error Message */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <div className="flex">
            <div className="text-red-600 text-sm">{error}</div>
            <button
              onClick={() => setError(null)}
              className="ml-auto text-red-400 hover:text-red-600"
            >
              ×
            </button>
          </div>
        </div>
      )}

      {/* Credentials Cards */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <CredentialCard
          environment="sandbox"
          title="Sandbox Credentials"
          icon={TestTube}
          formData={sandboxForm}
          setFormData={setSandboxForm}
          credential={credentials.sandbox}
        />

        <CredentialCard
          environment="live"
          title="Live Credentials"
          icon={Globe}
          formData={liveForm}
          setFormData={setLiveForm}
          credential={credentials.live}
        />
      </div>

      {/* Information Section */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
        <div className="flex items-start space-x-3">
          <Settings className="h-6 w-6 text-blue-600 mt-0.5" />
          <div>
            <h3 className="text-lg font-semibold text-blue-900 mb-2">
              Configuration Notes
            </h3>
            <ul className="text-blue-800 space-y-1 text-sm">
              <li>• Sandbox credentials are used for testing payments in the development environment</li>
              <li>• Live credentials are used for real transactions in production</li>
              <li>• Get your credentials from the <a href="https://developer.safaricom.co.ke/" target="_blank" rel="noopener noreferrer" className="underline hover:text-blue-600">Safaricom Developer Portal</a></li>
              <li>• Keep your credentials secure and never share them publicly</li>
              <li>• Changes take effect immediately after saving</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
}
